import { computed } from 'vue';
import { useFreedomCashbackQuery } from 'freedom-shared';
import { toSafeInteger, get } from 'lodash-es';
import { useI18n } from 'vue-i18n';

import { api } from '@/shared/api';
import { useUserStore } from '@/shared/model';

import {
  CASHBACK_BREAKDOWN_CONFIG,
  FREEDOM_LOYALTY_LEVELS,
  PARTNER_CASHBACK,
} from './config';
import type { CashbackDisplayDetails } from './types';
import starIcon from './assets/star.svg';

export const useFreedomCashbackDetails = () => {
  const { t } = useI18n();
  const userStore = useUserStore();

  const freedomCashbackQuery = useFreedomCashbackQuery({
    pfm: api,
    enabled: computed(() => !!userStore.user),
  });

  const isLoading = computed(() => freedomCashbackQuery.isFetching.value);
  const isError = computed(() => freedomCashbackQuery.isError.value);
  const totalCashback = computed(() =>
    toSafeInteger(freedomCashbackQuery.data.value?.total_cashback_percent),
  );

  const fallbackToLoyaltyLevelByPercent = () => {
    const percent = totalCashback.value - PARTNER_CASHBACK.amount;

    return (
      FREEDOM_LOYALTY_LEVELS.find(level => level.cashback === percent) || null
    );
  };

  const currentLoyaltyLevel = computed(() => {
    // 1. First trying to determine loyalty level by its name
    const levelNameFromApi = freedomCashbackQuery.data.value?.level_name;

    if (!levelNameFromApi) {
      // 2. If for whatever reason level name didn't come with the response
      return fallbackToLoyaltyLevelByPercent();
    }

    const loyaltyLevelInfo =
      FREEDOM_LOYALTY_LEVELS.find(
        level => level.title.toLowerCase() === levelNameFromApi.toLowerCase(),
        // 3. If somehow level name, we have got from backend, had changed
      ) || fallbackToLoyaltyLevelByPercent();

    return {
      ...loyaltyLevelInfo,
      limitPerTransaction: get(
        freedomCashbackQuery.data.value,
        'limit_per_transaction',
        loyaltyLevelInfo?.limitPerTransaction,
      ),
      limitPerMonth: get(
        freedomCashbackQuery.data.value,
        'limit_per_month',
        loyaltyLevelInfo?.limitPerMonth,
      ),
    };
  });

  const fallbackToStaticCashbackBreakdown = (): CashbackDisplayDetails[] => {
    const LOYALTY_LEVEL_CASHBACK = currentLoyaltyLevel.value
      ? {
          title: t('main.loyalty.details.breakdown.level.title'),
          subtitle: currentLoyaltyLevel.value.title,
          percentText: `${currentLoyaltyLevel.value.cashback}%`,
          icon: starIcon,
        }
      : null;

    const PARTNER_LOYALTY_CASHBACK = {
      title: t('main.loyalty.details.breakdown.partnership.title'),
      subtitle: t('main.loyalty.details.breakdown.partnership.subtitle'),
      percentText: `+${PARTNER_CASHBACK.amount}%`,
      icon: PARTNER_CASHBACK.icon,
    };

    return [
      ...(LOYALTY_LEVEL_CASHBACK ? [LOYALTY_LEVEL_CASHBACK] : []),
      PARTNER_LOYALTY_CASHBACK,
    ];
  };

  const cashbackBreakdown = computed(() => {
    const apiBreakdown = freedomCashbackQuery.data.value?.cashback_percents;

    if (!apiBreakdown) {
      return fallbackToStaticCashbackBreakdown();
    }

    return apiBreakdown.map(item => {
      const config = CASHBACK_BREAKDOWN_CONFIG.find(cfg =>
        // Проверяем, содержит ли имя из API хотя бы одно из наших ключевых слов
        cfg.keywords.some(keyword => item.name.toLowerCase().includes(keyword)),
      );

      if (!config) {
        return null;
      }

      const percentText =
        config?.key === 'level'
          ? `${item.percentage}%`
          : `+${item.percentage}%`;

      return {
        title: t(`main.loyalty.details.breakdown.${config?.key}.title`),
        percentText,
        icon: config?.icon,
        key: config?.key,
      } as CashbackDisplayDetails;
    });
  });

  return {
    isLoading,
    isError,
    currentLoyaltyLevel,
    totalCashback,
    cashbackBreakdown,
  };
};
